import { ArrowRight } from 'lucide-react';
import { PageStructuredData } from '@/components/seo/PageStructuredData';
import { StructuredData } from '@/components/seo/StructuredData';
import { CtaPanel, InShort, PageHero, PageShell, Reveal, Section, SmartLink } from '@/components/page/PageKit';
import { GLOSSARY } from '@/data/glossary';
import { CONTENT_REVIEWED_LABEL } from '@/seo/organization';
import { useSEO } from '@/hooks/useSEO';

const URL = 'https://beatview.ai/glossary';
const DESCRIPTION = 'Plain-English definitions of recruitment and AI hiring terms agencies use: AI recruiting agent, ATS, one-way video interview, structured interview, time to shortlist and more.';

const termSet = {
  '@context': 'https://schema.org',
  '@type': 'DefinedTermSet',
  '@id': `${URL}#terms`,
  name: 'Recruitment and AI hiring glossary',
  url: URL,
  hasDefinedTerm: GLOSSARY.map(t => ({ '@type': 'DefinedTerm', '@id': `${URL}#${t.slug}`, name: t.term, description: t.definition, url: `${URL}#${t.slug}`, inDefinedTermSet: `${URL}#terms` })),
};

const letters = Array.from(new Set(GLOSSARY.map(t => t.term[0].toUpperCase())));

export default function Glossary() {
  useSEO({ title: 'Recruitment & AI Hiring Glossary | Beatview', description: DESCRIPTION, canonicalUrl: URL });
  return (
    <PageShell>
      <PageStructuredData name="Recruitment and AI hiring glossary" description={DESCRIPTION} url={URL} breadcrumbs={[{ name: 'Resources', url: 'https://beatview.ai/resources' }, { name: 'Glossary' }]} />
      <StructuredData data={termSet} />
      <PageHero breadcrumbs={[{ name: 'Resources', href: '/resources' }, { name: 'Glossary' }]} eyebrow={`Glossary · Updated ${CONTENT_REVIEWED_LABEL}`} title={<>Recruitment and AI hiring <span className="bv-hero-highlight">glossary</span></>} description={`${GLOSSARY.length} terms recruitment agencies use every day, defined in plain English.`}>
        <nav aria-label="Glossary letters" className="flex flex-wrap gap-2">
          {letters.map(letter => <a key={letter} href={`#letter-${letter}`} className="flex h-10 w-10 items-center justify-center rounded-xl border border-bv-blue-line bg-white/80 font-display font-semibold text-bv-ink transition-colors hover:border-transparent hover:bg-bv-ink hover:text-white">{letter}</a>)}
        </nav>
      </PageHero>

      <Section tone="white" className="py-12 lg:py-14" narrow>
        <InShort label="How to use this glossary">Each definition is written to stand on its own, so you can share a link to a single term. Where a term relates to how Beatview works, the definition links to the relevant page.</InShort>
      </Section>

      <Section tone="wash" narrow>
        <dl className="space-y-4">
          {GLOSSARY.map((t, i) => {
            const firstOfLetter = i === 0 || GLOSSARY[i - 1].term[0].toUpperCase() !== t.term[0].toUpperCase();
            return (
              <Reveal key={t.slug}>
                <div id={t.slug} className="scroll-mt-28 rounded-3xl border border-bv-blue-line bg-white p-6 sm:p-7">
                  {firstOfLetter && <span id={`letter-${t.term[0].toUpperCase()}`} className="block scroll-mt-28" />}
                  <dt className="font-display text-xl font-semibold text-bv-ink"><a href={`#${t.slug}`} className="hover:text-bv-blue">{t.term}</a></dt>
                  <dd className="mt-2 text-[16px] leading-7 text-bv-ink2">{t.definition}</dd>
                  {t.href && <dd className="mt-3"><SmartLink href={t.href} className="group inline-flex items-center gap-1 text-sm font-semibold text-bv-blue">{t.linkLabel}<ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" /></SmartLink></dd>}
                </div>
              </Reveal>
            );
          })}
        </dl>
      </Section>

      <CtaPanel eyebrow="See the terms in practice" title={<>Watch an AI recruiting agent <span className="bv-hero-highlight">run a real role.</span></>} description="Start free with one active role, or book a demo with your own vacancy." secondary={{ label: 'Book a demo', href: '/contact' }} />
    </PageShell>
  );
}
